//depenencies
import * as Sentry from "@sentry/react";
import React, { useEffect, useMemo, useState } from "react";

import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";

import { toast } from "@performyard/py-components";
// service contracts
import { Alert } from "@performyard/py-components";
import { EmployeeCustomFieldUpdate } from "@performyard/service-contracts";
import { isSyntheticEmail } from "@performyard/utils-isomorphic";

//components
import Button from "../../../../../components/Button";
import Card from "../../../../../components/Card";
import Container from "../../../../../components/Container";
import Input from "../../../../../components/Inputs/InputText";
import Spacer from "../../../../../components/Spacer";
import FieldErrorFallback from "./FieldErrorFallback";

//hooks
import {
	useEmailTokens,
	useEmployeeCustomFields,
	useMutateEmployeeCustomFields,
	useRbacEmployeePermissions,
} from "../../../../../api/hooks";
import useForm from "../../../../../hooks/useForm";
import useSearchQueryParams from "../../../../../hooks/useSearchQueryParams";

//utils
import { appendCurrentLocationAsFrom } from "../../../../../utils/employeeDetailsBreadcrumbs";
import {
	getEditEmployeeCustomFieldsSchema,
	getFieldInput,
	getIntegrationImage,
	getValueForInputType,
	getValueForUpdate,
} from "../../../utils";

//config
import Config from "../../../Config";
//css
import css from "./EditEmployee.module.scss";

type EditEmployeeCardProps = {
	employeeId: string;
};

const EditEmployeeCard = ({ employeeId }: EditEmployeeCardProps) => {
	const { queryParams, handleQueryChange } = useSearchQueryParams({ schema: Config.viewPageSchema });
	const search = queryParams.search || "";
	const setSearch = (value: string) => handleQueryChange({ search: value });
	const [isCanceling, setIsCancelling] = useState(false);
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

	const { data = { customFields: [] } } = useEmployeeCustomFields(employeeId);

	const isEmployeeManagedByIntegration = data.customFields.some((fieldData) => {
		const userExternalIdKey = fieldData?.user?.externalIds && Object.keys(fieldData?.user?.externalIds)?.[0];
		const userIsActivelyConnected = userExternalIdKey ? fieldData?.integrations?.[userExternalIdKey] : false;

		return (
			fieldData.isScimManaged ||
			(userIsActivelyConnected &&
				fieldData?.integrations &&
				Object.values(fieldData?.integrations)?.some((integrationBoolean) => integrationBoolean === true))
		);
	});

	const {
		mutate: updateEmployee,
		isPending,
		isSuccess,
	} = useMutateEmployeeCustomFields(() => {
		setInitialState(state);
		toast.success(`Employee Updated`);
		handleQueryChange({ search: undefined, editing: undefined });
	});

	const { data: rbac } = useRbacEmployeePermissions(employeeId, "employee_details");

	const canEmployeeViewHistory =
		rbac?.includes("employee_details:viewAdminFieldHistory") ||
		rbac?.includes("employee_details:viewSelfFieldHistory") ||
		rbac?.includes("employee_details:viewManagerFieldHistory");

	const { data: emailTokens } = useEmailTokens(employeeId);
	const isPendingEmailChange = (emailTokens?.tokens?.length || 0) > 0;

	const customFields = data.customFields.filter(
		(field) => field.editable || (field.integrations && Object.keys(field.integrations).length > 0)
	);
	// create a schema that maps the cusom fields to a zod type
	const schema = getEditEmployeeCustomFieldsSchema(customFields);
	const {
		validate,
		state,
		initialState,
		updateForm,
		resetForm,
		setNewDefaultValues,
		setInitialState,
		hasUnsavedChanges,
	} = useForm({
		schema: schema,
		defaultValues: {},
		disableWarning: isPending || isSuccess || isCanceling,
	});

	const validation = useMemo(() => validate(), [state]);

	useEffect(() => {
		// flushSync: false because flushing from inside an effect logs a React warning and
		// downgrades to a normal-priority update anyway; nothing here is latency sensitive.
		if (isCanceling) {
			handleQueryChange(
				{ search: "", editing: undefined },
				{ replace: true, preventScrollReset: true, flushSync: false }
			);
			resetForm();
			setIsCancelling(false);
		}
	}, [isCanceling]);

	//after the fields are loaded, we should set the form values and states
	useEffect(() => {
		const loadedState = {
			customFields: Object.fromEntries(
				customFields.map((field) => {
					return [field._id, getValueForInputType(field.type, field.value)];
				})
			),
		};
		setNewDefaultValues(loadedState);
		setInitialState(loadedState);
	}, [JSON.stringify(customFields)]);

	//grab static fields
	const firstNameField = customFields.find((field) => field.name === "First Name");
	const lastNameField = customFields.find((field) => field.name === "Last Name");
	const emailField = customFields.find((field) => field.name === "Email");
	const nickNameField = customFields.find((field) => field.name === "Nickname");

	// Synthetic users (placeholder @noemail.performyard.com emails) cannot
	// have their email edited via this form — the server rejects the save
	// and the email is regenerated from the company's Alternate ID field.
	// See docs/guides/synthetic-emails.md.
	const isUserSyntheticEmail = isSyntheticEmail(emailField?.value as string | undefined);

	const handleUpdateEmployee = () => {
		const formattedUpdates: EmployeeCustomFieldUpdate[] = [];

		for (const [fieldId, fieldValue] of Object.entries(state.customFields)) {
			const initialFormValue = initialState.customFields[fieldId];
			const field = customFields.find((field) => field._id.toString() === fieldId);
			if (field && JSON.stringify(fieldValue) !== JSON.stringify(initialFormValue)) {
				formattedUpdates.push({
					_id: field._id,
					value: getValueForUpdate(field.type, fieldValue),
				});
			}
		}

		updateEmployee({
			employeeId: employeeId,
			updates: formattedUpdates,
			asOf: new Date(),
		});
	};

	const canUpdateForm = hasUnsavedChanges;

	const handleFieldError = (fieldId: string, fieldName: string, error: Error) => {
		setFieldErrors((prev) => ({
			...prev,
			[fieldId]: `Error rendering ${fieldName}: ${error.message}`,
		}));
	};

	const getField = (field, alertMessage: string = "") => {
		if (
			!search ||
			field.tags?.some((tag) => tag.toLowerCase().includes(search.toLowerCase())) ||
			field.name.toLowerCase().includes(search.toLowerCase())
		) {
			const fieldId = field._id.toString();
			const hasFieldError = !!fieldErrors[fieldId];
			const hasIntegration = isEmployeeManagedByIntegration && !!getIntegrationImage(field?.integrations);
			const isFieldDisabled =
				!field?.editable ||
				field?.isScimManaged ||
				hasIntegration ||
				(field.name === "Email" && isUserSyntheticEmail);

			return (
				<div key={fieldId} data-field-disabled={isFieldDisabled || undefined}>
					<Container block testId={field.name.toLowerCase().replace(/\s+/g, "-")}>
						<Sentry.ErrorBoundary
							fallback={
								<FieldErrorFallback
									field={field}
									onUpdate={(value) => {
										updateForm({
											customFields: { ...state.customFields, [fieldId]: value?.fieldValue },
										});
									}}
								/>
							}
							onError={(error) => {
								handleFieldError(fieldId, field.name, error as Error);
								Sentry.setContext("field", {
									fieldId,
									fieldName: field.name,
									fieldType: field.type,
									employeeId,
								});
							}}
						>
							{getFieldInput(
								field,
								state.customFields?.[field?._id] || null,
								(value) => {
									updateForm({
										customFields: { ...state.customFields, [fieldId]: value?.fieldValue },
									});
								},
								!!validation?.errors[`customFields.${fieldId}`],
								isEmployeeManagedByIntegration,
								field?.name,
								{
									disabled:
										!field?.editable ||
										field?.isScimManaged ||
										(field.name === "Email" && isUserSyntheticEmail),
									labelHref: canEmployeeViewHistory
										? appendCurrentLocationAsFrom(`/view-field-history/${fieldId}/${employeeId}`)
										: undefined,
								}
							)}
						</Sentry.ErrorBoundary>
						{!hasFieldError && alertMessage ? (
							<div>
								<Spacer height={1} />
								<Alert type="error" message={alertMessage} weight="semi-bold" items="start" />
							</div>
						) : null}
						<Spacer height={2} />
					</Container>
				</div>
			);
		}
	};

	const content = (
		<Card>
			<style>{`
				[data-edit-employee] label,
				[data-edit-employee] label * { font-weight: 600 !important; font-size: 14px !important; }
				[data-edit-employee] label button { font: inherit; font-weight: 600 !important; }
				[data-field-disabled] label,
				[data-field-disabled] label * { color: var(--py-neutral-5) !important; }
			`}</style>
			<form
				data-edit-employee
				onSubmit={(event) => {
					event.preventDefault();
					if (canUpdateForm) {
						handleUpdateEmployee();
					}
				}}
			>
				<Container horizontalAlignment="space-between">
					<Input
						value={search}
						className={css.search}
						onChange={(value) => setSearch(value)}
						icon={faMagnifyingGlass}
						placeholder="Search fields..."
						ariaLabel="Search fields"
					/>
					<Container gap="8px">
						<Button
							color="blue"
							onClick={() => {
								setIsCancelling(true);
							}}
						>
							Cancel
						</Button>
						<Button color="green" isRequesting={isPending} disabled={!canUpdateForm} submit>
							Save
						</Button>
					</Container>
				</Container>
				<Spacer height={4} />

				<div>
					{firstNameField && getField(firstNameField)}
					{lastNameField && getField(lastNameField)}
					{emailField &&
						getField(
							emailField,
							isPendingEmailChange ? "Email address change is pending verification" : ""
						)}
					{nickNameField && getField(nickNameField)}
					{customFields
						.filter((field) => !Config.STATIC_FIELDS.includes(field.name))
						.map((field) => {
							return <div key={field._id.toString()}>{getField(field)}</div>;
						})}
				</div>
				<Container gap={"16px"}>
					<Button
						size="medium"
						style={{ width: "100%" }}
						color="blue"
						onClick={() => {
							setIsCancelling(true);
						}}
					>
						Cancel
					</Button>
					<Button
						color="green"
						size="medium"
						style={{ width: "100%" }}
						isRequesting={isPending}
						disabled={!canUpdateForm}
						submit
					>
						Save Changes
					</Button>
				</Container>
			</form>
		</Card>
	);

	return content;
};

export default EditEmployeeCard;
